import { styled } from "styled-components";
import { cssFontSize, cssSpacing } from "../../util/styles.js";

export interface MultiSelectOption {
  label: string;
  value: string;
  count?: number;
}

export interface MultiSelectProps {
  label?: string;
  options: MultiSelectOption[];
  selected: string[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
}

function getLabel(
  selected: string[],
  options: MultiSelectOption[],
  placeholder: string
) {
  if (selected.length === 0) return placeholder;
  return selected
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean)
    .join(", ");
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  selected,
  isOpen,
  onToggle,
  onSelect,
  onClear,
  placeholder = "Vis alle",
}) => (
  <Group>
    {label && <GroupLabel>{label}</GroupLabel>}
    <Trigger
      $active={selected.length > 0}
      $open={isOpen}
      onClick={onToggle}
      type="button"
      aria-expanded={isOpen}
    >
      <span>{getLabel(selected, options, placeholder)}</span>
      <Chevron $open={isOpen}>
        <ChevronIcon />
      </Chevron>
    </Trigger>
    {isOpen && (
      <Menu>
        <Option
          $active={selected.length === 0}
          type="button"
          aria-pressed={selected.length === 0}
          onClick={onClear}
        >
          {placeholder}
        </Option>
        {options.map((option) => (
          <Option
            key={option.value}
            $active={selected.includes(option.value)}
            type="button"
            aria-pressed={selected.includes(option.value)}
            onClick={() => onSelect(option.value)}
          >
            {option.label}
            {option.count !== undefined ? ` (${option.count})` : ""}
          </Option>
        ))}
      </Menu>
    )}
  </Group>
);

const Group = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 200px;
  max-width: 200px;
  flex: 1;
`;

export const GroupLabel = styled.span`
  display: block;
  font-size: ${cssFontSize("tiny")};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary-text-color);
`;

const Trigger = styled.button<{ $active?: boolean; $open?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${cssSpacing("xs")};
  padding: ${cssSpacing("xs")} ${cssSpacing("s")};
  border: 2px solid
    ${(p) =>
      p.$active || p.$open
        ? "var(--multiselect-accent-color)"
        : "var(--multiselect-border-color)"};
  border-radius: ${(p) => p.theme.borderRadius};
  border-bottom-left-radius: ${(p) => (p.$open ? "0" : "")};
  border-bottom-right-radius: ${(p) => (p.$open ? "0" : "")};
  background: ${(p) =>
    p.$active
      ? "var(--multiselect-border-color)"
      : "var(--multiselect-background)"};
  color: var(--multiselect-accent-color);
  font-size: ${cssFontSize("small")};
  font-family: inherit;
  font-weight: ${(p) => (p.$active ? "700" : "400")};
  cursor: pointer;
  width: 100%;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Chevron = styled.span<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.15s;
  transform: ${(p) => (p.$open ? "rotate(180deg)" : "rotate(0deg)")};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;

    path {
      fill: none;
    }
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% - 2px);
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--multiselect-menu-background);
  border: 2px solid var(--multiselect-accent-color);
  border-top: none;
  border-radius: 0 0 ${(p) => p.theme.borderRadius} ${(p) => p.theme.borderRadius};
  overflow: hidden;
`;

const Option = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px ${cssSpacing("s")};
  background: ${(p) =>
    p.$active
      ? "var(--multiselect-background)"
      : "var(--multiselect-menu-background)"};
  border: none;
  border-bottom: 1px solid var(--multiselect-border-color);
  color: var(--multiselect-accent-color);
  font-size: ${cssFontSize("small")};
  font-family: inherit;
  font-weight: ${(p) => (p.$active ? "700" : "400")};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--multiselect-background);
  }
`;

function ChevronIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 9L12 15L18 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
